import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useGaugeState } from "@mui/x-charts"
import { useTheme } from "@mui/material"

import type { GaugeConfigValues } from "types/gaugePlot.types"
import { getNumericFormatter } from "helpers/formatters/plots"

interface MuiGaugeLabelProps {
  values: GaugeConfigValues
  size: number
  decimalPlaces: number
}

const MuiGaugeLabel = ({ values, size, decimalPlaces }: MuiGaugeLabelProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage === "en" ? "en-gb" : i18n.language
  const theme = useTheme()
  const { cx, cy } = useGaugeState()

  const numericFormatter = useMemo(
    () => getNumericFormatter(locale, decimalPlaces),
    [locale, decimalPlaces],
  )

  const valueFontSize = size / 11
  const labelFontSize = size / 28

  return (
    <>
      {values.value !== undefined && (
        <text
          x={cx}
          y={cy - 10}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: values.fill }}
          fontSize={valueFontSize}
          fontWeight={700}
        >
          {values.prefix ? `${values.prefix} ` : ""}
          {numericFormatter(values.value)}
          {values.suffix ? ` ${values.suffix}` : ""}
        </text>
      )}
      {values.legend !== undefined && (
        <text
          x={cx}
          y={cy + 25}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: values.fill }}
          fontSize={labelFontSize}
          fontWeight={500}
        >
          {t(`device_information.${values.legend.toUpperCase()}`, {
            ...values.legendParts,
            defaultValue: values.legend,
          })}
        </text>
      )}
      {values.footer && (
        <text
          x={cx}
          y={cy + 120}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: theme.palette.text.secondary }}
          fontSize={labelFontSize}
          fontWeight={400}
        >
          {values.footer}
        </text>
      )}
    </>
  )
}

export default MuiGaugeLabel
